import { EpticaHelper } from '@agent/core/channel-digit/mail/eptica/eptica.helper';
import { Injectable } from '@angular/core';
import { delay, first, interval, map, Observable, retryWhen, Subscription, switchMap, tap, withLatestFrom } from 'rxjs';

import {
  EPTICA_LOGIN_FRAME_NAME,
  EpticaInfo,
  EpticaInfoFacade,
  EpticaSessionFacade,
  EpticaStrategyService,
  EpticaUserFacade,
  HttpService,
  isNotNullOrUndefined,
  Logger,
  LOGIN_PAGE_NAME,
  ToasterFacade,
  ZoneService,
} from '@contact/upgrade';

import { AppExternalService } from '../ui/app-external.service';

const RETRY_DELAY = 5000;
const PING_TIMER = 900000;

@Injectable({
  providedIn: 'root',
})
export class EpticaFrameIntegrationService implements EpticaStrategyService {
  // Subscription ping observable to keep session
  private pingSub = new Subscription();

  constructor(
    private readonly appExternalService: AppExternalService,
    private readonly toasterFacade: ToasterFacade,
    private readonly zoneService: ZoneService,
    private readonly logger: Logger,
    private readonly http: HttpService,
    private readonly epticaInfoFacade: EpticaInfoFacade,
    private readonly epticaSessionFacade: EpticaSessionFacade,
    private readonly epticaUserFacade: EpticaUserFacade
  ) {
    this.logger = this.logger.forSource('EpticaFrameIntegrationService');
  }

  /**
   * Initialize eptica processing
   */
  initialize(): Observable<any> {
    this.logger.info(`initialization : start`);
    this.epticaInfoFacade.load();
    this.epticaSessionFacade.authenticate();
    // process initialize
    const login$ = this.epticaInfoFacade.waitLoaded$.pipe(
      withLatestFrom(this.epticaInfoFacade.info$.pipe(isNotNullOrUndefined()), (_, info) => info),
      tap(data => this.logger.info(`initialization : connection data[${JSON.stringify(data)}].`)),
      switchMap(data =>
        this.loginByFrame(data).pipe(
          tap(accountId => this.logger.info(`initialization : authenticated with account id[${accountId}].`)),
          switchMap(accountId => {
            this.epticaUserFacade.load(accountId);
            return this.epticaUserFacade.waitLoaded$;
          })
        )
      )
    );
    // retry mechanism if intialize failed
    let retry = 0;
    const retry$ = login$.pipe(
      retryWhen(errors =>
        errors.pipe(
          tap(err => this.logger.error(`initialization : failed with error[${JSON.stringify(err)}]. Retry after [${RETRY_DELAY} ms]`)),
          delay(RETRY_DELAY),
          tap(err => {
            if (retry++ >= 3) {
              throw err;
            }
          })
        )
      )
    );
    // ping mechanism to keep session open
    const ping$ = interval(PING_TIMER, this.zoneService.leaveScheduler()).pipe(
      withLatestFrom(this.epticaInfoFacade.info$.pipe(isNotNullOrUndefined()), (_, { iasUrl }) => iasUrl),
      switchMap(iasUrl => this.http.get(`${iasUrl}/api/ping.action`))
    );
    // subscribe
    return retry$.pipe(
      tap({
        error: () => {
          this.logger.error(`initialization : failed`);
          this.toasterFacade.alert({
            message: 'agent:toast.mail_login_failed',
          });
        },
        complete: () => {
          this.logger.info(`initialization : done`);
          this.pingSub.unsubscribe();
          this.pingSub = ping$.subscribe();
        },
      })
    );
  }

  private loginByFrame(info: EpticaInfo): Observable<string> {
    const frame = this.appExternalService.createApplication(EPTICA_LOGIN_FRAME_NAME);
    if (frame.isEmpty()) {
      throw new Error('frame not found');
    }
    this.logger.info(`initialization : / - login frame created with name[${frame.get().name}]`);
    // submit form
    this.logger.debug(`initialization : / - submit credentials [${info.iasUrl}, ${info.login}, ***]`);
    EpticaHelper.submitLoginForm(info.iasUrl, info.login, info.password);
    return EpticaHelper.waitForDataObs(frame.get(), 500, 2).pipe(
      first(data => data.pageName !== LOGIN_PAGE_NAME),
      map(data => {
        if (data.pageContext.account) {
          return data.pageContext.account;
        }
        throw new Error('account not found');
      }),
      tap({
        next: () => this.logger.info(`initialization : / - checking that user is authenticated`),
        error: err => {
          this.logger.error(`initialization : / - login frame failed with error[${JSON.stringify(err)}]`);
          this.appExternalService.removeApplication(EPTICA_LOGIN_FRAME_NAME);
        },
      })
    );
  }
}
